'use client';

import { MarketType } from '@drift-labs/sdk';
import { UIMarket, matchEnum } from '@drift/common';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

/**
 * Hook for switching between different markets and managing market state.
 */
export const useSwitchMarket = () => {
	const router = useRouter();
	const selectedMarket = useDriftStore((s) => s.selectedMarket);
	const tradePageTab = useDriftStore((s) => s.tradePageTable.currentTab);
	const setDriftStore = useDriftStore((s) => s.set);

	const switchMarket = useCallback(
		({
			marketIndex,
			marketType,
			goToTradePage,
		}: {
			marketIndex: number;
			marketType: MarketType;
			goToTradePage?: boolean;
		}) => {
			const newMarket = new UIMarket(marketIndex, marketType);
			const currentMarket = selectedMarket?.current;

			if (!newMarket || !currentMarket?.marketId) {
				return;
			}

			const currentSearchParams = new URLSearchParams(window.location.search);

			const isCurrentlyOnTradePage =
				window.location.pathname === '/' ||
				window.location.pathname === `/${currentMarket.market?.symbol ?? ''}`;
			const isSwitchingToCurrentMarket = currentMarket.equals(newMarket);

			if (!isSwitchingToCurrentMarket) {
				if (isCurrentlyOnTradePage) {
					// We don't want to use router.push here because that causes a full remount of the page when already on the trade page
					// Instead we just manipulate the history. More info here: https://github.com/vercel/next.js/discussions/50711
					window.history.pushState(
						null,
						'',
						`/${newMarket.market.symbol}${
							currentSearchParams.size > 0
								? `?${currentSearchParams.toString()}`
								: ''
						}`
					);
				} else if (goToTradePage) {
					router.push(
						`/${newMarket.market.symbol}${
							currentSearchParams.size > 0
								? `?${currentSearchParams.toString()}`
								: ''
						}`
					);
				}
			} else if (goToTradePage) {
				router.push(
					`/${newMarket.market.symbol}${
						currentSearchParams.size > 0
							? `?${currentSearchParams.toString()}`
							: ''
					}`
				);
			}

			const newMarketIsSpot = matchEnum(newMarket?.marketType, MarketType.SPOT);
			const currentMarketIsSpot = matchEnum(
				currentMarket?.marketType,
				MarketType.SPOT
			);

			// DPE-1073: Switch tab between positions and balances when switching between perp and spot
			// If the tab was already on something else or the market type didn't change, then don't switch it
			if (
				!currentMarketIsSpot &&
				newMarketIsSpot &&
				tradePageTab === 'positions'
			) {
				setDriftStore((s) => {
					s.tradePageTable.currentTab = 'balances';
				});
			} else if (
				currentMarketIsSpot &&
				!newMarketIsSpot &&
				tradePageTab === 'balances'
			) {
				setDriftStore((s) => {
					s.tradePageTable.currentTab = 'positions';
				});
			}

			if (
				currentMarket &&
				currentMarket.marketId &&
				!newMarket.equals(currentMarket)
			) {
				setDriftStore((s) => {
					s.selectedMarket.current = newMarket;
					s.selectedMarket.name = newMarket.marketName;
					s.selectedMarket.marketId = newMarket.marketId;
				});
				// reset the saved limit price only when market actually changes
				setDriftStore((s) => {
					s.tradeForm.savedLimitPrice = null;
				});
			}
		},
		[selectedMarket, tradePageTab, setDriftStore, router]
	);

	return { switchMarket };
};
